import React, { useEffect, useState, useCallback } from 'react';
import Image from '../../../components/appImage';
import Button from '../../../components/ui/button';
import Icon from '../../../components/appIcon';
import StatusBadge from './statusBadge';
import PPEStatusBadge from '../../../components/ppeStatusBadge';
import PPEItemsList from '../../../components/ppeItemsList';
import attendanceApi from 'services/api/attendanceApi';
import { useAuth } from 'components/ui/navigationGuard';

const EmployeeAttendanceModal = ({
  employee,
  attendanceSummary,
  attendanceRecords,
  timeFilter,
  onClose
}) => {
  const { user } = useAuth(); // Get the current user to access company_id
  const [attMaster, setAttMaster] = useState(null);
  const [attTransaction, setAttTransaction] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState({ start: '', end: '' });

  // Calculate date range based on time filter
  const calculateDateRange = useCallback(() => {
    const today = new Date();
    let startDate = new Date();
    let endDate = new Date();
    
    switch (timeFilter) {
      case 'today':
        startDate = today;
        endDate = today;
        break;
      case 'week':
        // Start of current week (Sunday)
        const dayOfWeek = today.getDay();
        startDate = new Date(today);
        startDate.setDate(today.getDate() - dayOfWeek);
        // End of current week (Saturday)
        endDate = new Date(startDate);
        endDate.setDate(startDate.getDate() + 6);
        break;
      case 'month':
        // Start of current month
        startDate = new Date(today.getFullYear(), today.getMonth(), 1);
        // End of current month
        endDate = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        break;
      case 'year':
        // Start of current year
        startDate = new Date(today.getFullYear(), 0, 1);
        // End of current year
        endDate = new Date(today.getFullYear(), 11, 31);
        break;
      default:
        startDate = today;
        endDate = today;
    }
    
    // Format dates as YYYY-MM-DD
    const formatDate = (date) => {
      return date.toISOString().split('T')[0];
    };
    
    return {
      start: formatDate(startDate),
      end: formatDate(endDate)
    };
  }, [timeFilter]);

  useEffect(() => {
    if (!employee?.employee_id || !user?.company_id) return;

    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Calculate date range based on time filter
        const range = calculateDateRange();
        setDateRange(range);
        
        // Fetch attendance master data for the date range
        const masterData = await attendanceApi.getAttendanceMasterByDateRange(
          employee.employee_id,
          user.company_id,
          range.start,
          range.end
        );
        setAttMaster(masterData);
        
        // Fetch attendance transaction data for the current month
        const now = new Date();
        const month = now.getMonth() + 1;
        const year = now.getFullYear();
        
        const transactionData = await attendanceApi.getAttendanceTransaction(
          employee.employee_id,
          month,
          year,
          user.company_id
        );
        setAttTransaction(transactionData);
      } catch (err) {
        console.error("Failed to fetch attendance data", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [employee, user?.company_id, timeFilter, calculateDateRange]);

  // Format time filter for display
  const formatTimeFilter = (filter) => {
    switch (filter) {
      case 'today': return 'Today';
      case 'week': return 'This Week';
      case 'month': return 'This Month';
      case 'year': return 'This Year';
      default: return filter;
    }
  };

  // Calculate total working hours
  const calculateTotalWorkingHours = () => {
    if (!attendanceRecords || attendanceRecords.length === 0) return '0h 0m';

    let totalMinutes = 0;
    attendanceRecords.forEach(record => {
      if (record.checkIn && record.checkOut) {
        const duration = new Date(record.checkOut) - new Date(record.checkIn);
        totalMinutes += Math.floor(duration / 60000); // Convert to minutes
      }
    });

    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${hours}h ${minutes}m`;
  };

  // Get current summary based on time filter
  const getCurrentSummary = () => {
    if (!attendanceSummary) return null;
    return attendanceSummary[timeFilter] || {};
  };

  const currentSummary = getCurrentSummary();

  // Parse PPE items if they're strings
  const parsePPEItems = (items) => {
    if (!items) return [];
    return typeof items === 'string' ? JSON.parse(items) : items;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-lg w-full max-w-6xl max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center space-x-4">
              <div className="w-16 h-16 bg-muted rounded-full overflow-hidden">
                <Image
                  src={`${import.meta.env.VITE_API_URL}${employee?.imageUrl}` || employee?.image1}
                  alt={`${employee?.first_name} ${employee?.last_name}`}
                  className="w-full h-full object-cover"
                />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-foreground">
                  {employee?.first_name} {employee?.last_name}
                </h2>
                <p className="text-muted-foreground">{employee?.employee_id} • {employee?.department?.name || 'No Department'}</p>
              </div>
            </div>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <Icon name="X" size={24} />
            </Button>
          </div>

          {/* Time Period Selector */}
          <div className="mb-6">
            <h3 className="text-lg font-medium text-foreground mb-2">
              Time Period: {formatTimeFilter(timeFilter)}
              {dateRange.start && dateRange.end && (
                <span className="text-sm text-muted-foreground ml-2">
                  ({dateRange.start} to {dateRange.end})
                </span>
              )}
            </h3>
          </div>

          {/* Summary Stats */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
            <div className="bg-muted p-4 rounded-lg">
              <div className="flex items-center space-x-2 mb-1">
                <Icon name="Clock" size={16} className="text-primary" />
                <div className="text-sm text-muted-foreground">Total Working Hours</div>
              </div>
              <div className="text-2xl font-bold text-foreground">
                {attMaster?.totalWorkingHours || calculateTotalWorkingHours()}
              </div>
            </div>
            <div className="bg-muted p-4 rounded-lg">
              <div className="flex items-center space-x-2 mb-1">
                <Icon name="CheckCircle" size={16} className="text-green-500" />
                <div className="text-sm text-muted-foreground">Present Days</div>
              </div>
              <div className="text-2xl font-bold text-foreground">
                {attTransaction?.noOfPresent || currentSummary?.total_present || 0}
              </div>
            </div>
            <div className="bg-muted p-4 rounded-lg">
              <div className="flex items-center space-x-2 mb-1">
                <Icon name="XCircle" size={16} className="text-red-500" />
                <div className="text-sm text-muted-foreground">Absent Days</div>
              </div>
              <div className="text-2xl font-bold text-foreground">
                {attTransaction?.noOfAbsent || currentSummary?.absent || 0}
              </div>
            </div>
            <div className="bg-muted p-4 rounded-lg">
              <div className="flex items-center space-x-2 mb-1">
                <Icon name="LogIn" size={16} className="text-blue-500" />
                <div className="text-sm text-muted-foreground">First Check-In</div>
              </div>
              <div className="text-lg font-bold text-foreground">
                {attMaster?.firstCheckIn 
                  ? new Date(attMaster.firstCheckIn).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                  : '--'}
              </div>
            </div>
            <div className="bg-muted p-4 rounded-lg">
              <div className="flex items-center space-x-2 mb-1">
                <Icon name="LogOut" size={16} className="text-purple-500" />
                <div className="text-sm text-muted-foreground">Last Check-Out</div>
              </div>
              <div className="text-lg font-bold text-foreground">
                {attMaster?.lastCheckOut 
                  ? new Date(attMaster.lastCheckOut).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                  : '--'}
              </div>
            </div>
          </div>

          {/* Daily Records (if available) */}
          {attMaster?.dailyRecords && attMaster.dailyRecords.length > 0 && (
            <div className="mb-6">
              <h3 className="text-lg font-medium text-foreground mb-4">Daily Attendance Summary</h3>
              <div className="bg-card border border-border rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-muted">
                      <tr>
                        <th className="text-left p-3">Date</th>
                        <th className="text-left p-3">Check In</th>
                        <th className="text-left p-3">Check Out</th>
                        <th className="text-left p-3">Working Hours</th>
                      </tr>
                    </thead>
                    <tbody>
                      {attMaster.dailyRecords.map((record, index) => (
                        <tr key={index} className="border-b border-border hover:bg-muted/50">
                          <td className="p-3">
                            {new Date(record.date).toLocaleDateString()}
                          </td>
                          <td className="p-3">
                            {record.checkIn 
                              ? new Date(record.checkIn).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                              : '--'}
                          </td>
                          <td className="p-3">
                            {record.checkOut 
                              ? new Date(record.checkOut).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                              : '--'}
                          </td>
                          <td className="p-3">
                            {record.workingHours || '--'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Attendance Records Table */}
          <div className="bg-card border border-border rounded-lg overflow-hidden">
            <div className="p-4 border-b border-border">
              <h3 className="text-lg font-medium text-foreground">Attendance Records</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted">
                  <tr>
                    <th className="text-left p-3">Date</th>
                    <th className="text-left p-3">Check In</th>
                    <th className="text-left p-3">Check Out</th>
                    <th className="text-left p-3">Duration</th>
                    <th className="text-left p-3">Status</th>
                    <th className="text-left p-3">Image</th>
                  </tr>
                </thead>
                <tbody>
                  {attendanceRecords && attendanceRecords?.length > 0 ? (
                    attendanceRecords?.map(record => {
                      // Parse PPE items for this record
                      const missingPpeItems = parsePPEItems(record?.missingPpeItems);
                      const detectedPpeItems = parsePPEItems(record?.detectedPpeItems);

                      return (
                        <tr key={record.id} className="border-b border-border hover:bg-muted/50">
                          <td className="p-3">
                            {record?.checkIn ? new Date(record?.checkIn).toLocaleDateString() : new Date(record?.checkOut).toLocaleDateString()}
                          </td>
                          <td className="p-3">
                            {record?.checkIn ? new Date(record?.checkIn).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '--'}
                          </td>
                          <td className="p-3">
                            {record?.checkOut ? new Date(record?.checkOut).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '--'}
                          </td>
                          <td className="p-3">
                            {record?.workingHours || '--'}
                          </td>
                          <td className="p-3">
                            <StatusBadge status={record?.status} />
                          </td>
                          <td className="p-3">
                            {record?.captureImage ? (
                              <div className="w-24 h-24 bg-muted rounded overflow-hidden">
                                <Image
                                  src={`${import.meta.env.VITE_API_URL}${record?.captureImage}`}
                                  alt="Attendance capture"
                                  className=" w-full cursor-pointer hover:opacity-80 h-full"
                                />
                              </div>
                            ) : (
                              <div className="w-12 h-12 bg-muted rounded flex items-center justify-center">
                                <Icon name="ImageOff" size={16} className="text-muted-foreground" />
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="6" className="p-8 text-center">
                        <div className="text-muted-foreground">No attendance records found for this period</div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="mt-6 flex justify-end">
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmployeeAttendanceModal;